import './polyfills';

import App from './app';
import { SENTRY_DSN, SENTRY_ENV, SENTRY_TUNNEL } from './config-global';
import {
  clearAssetRecoveryAttempts,
  isRecoverableAssetLoadError,
  requestAssetRecovery,
} from './utils/asset-recovery';
import { bootstrapServiceWorker } from './utils/bootstrap-sw';
import { registerGlobalErrorListeners } from './utils/error-listeners';

import * as Sentry from '@sentry/react';
import { Suspense } from 'react';
import ReactDOM from 'react-dom/client';
import { HelmetProvider } from 'react-helmet-async';
import { BrowserRouter } from 'react-router';

Sentry.init({
  dsn: SENTRY_DSN,
  environment: SENTRY_ENV,
  enabled: !!SENTRY_ENV,
  tunnel: SENTRY_TUNNEL,
  integrations: [Sentry.browserTracingIntegration()],
  // Performance Monitoring
  tracesSampleRate: 1.0,
  tracePropagationTargets: ['localhost', /^https:\/\/api\.geartracker\.net/],
  ignoreErrors: [
    // html5-qrcode calls video.play() without catching the promise: closing the
    // scanner before the camera has started removes the video from the DOM and
    // rejects with AbortError. Harmless, and not catchable on our side.
    /The play\(\) request was interrupted/,
  ],
  denyUrls: [
    // Third-party scripts injected into the page (tag manager / consent
    // platform, browser extensions): none of this is our code.
    /gtm\.js/i,
    /googletagmanager\.com/i,
    /^chrome-extension:\/\//i,
    /^moz-extension:\/\//i,
    /^safari-(web-)?extension:\/\//i,
  ],
});

// used to react to unhandled errors outside of React
registerGlobalErrorListeners();
bootstrapServiceWorker();

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement,
);

const ErrorFallback = ({ error }: { error: Error }) => {
  console.error(error);
  if (isRecoverableAssetLoadError(error)) {
    const recoveryAction = requestAssetRecovery('react-error-boundary', error);

    if (recoveryAction !== 'exhausted') {
      return (
        <div role="alert" style={{ padding: '2rem', textAlign: 'center' }}>
          <h2>Loading new version...</h2>
          <p>
            The application has been updated. Reloading with latest assets...
          </p>
        </div>
      );
    }

    // If we've already tried reloading and still hit an error, show error UI
    return (
      <div role="alert" style={{ padding: '2rem', textAlign: 'center' }}>
        <h2>Unable to load application</h2>
        <p>
          There was a problem loading the latest version. This might be due to a
          network issue.
        </p>
        <button
          type="button"
          onClick={() => {
            clearAssetRecoveryAttempts();
            window.location.reload();
          }}
          style={{ padding: '0.5rem 1rem', cursor: 'pointer' }}
        >
          Try Again
        </button>
      </div>
    );
  }
  return (
    <div role="alert">
      <h2>Something went wrong</h2>
      <pre>{error.message}</pre>
      <button
        type="button"
        onClick={() => {
          window.location.href = '/';
        }}
      >
        Reload
      </button>
    </div>
  );
};

root.render(
  <HelmetProvider>
    <BrowserRouter>
      <Suspense>
        {/* <StrictMode> */}
        <Sentry.ErrorBoundary
          fallback={({ error }) => <ErrorFallback error={error as Error} />}
        >
          <App />
        </Sentry.ErrorBoundary>
        {/* </StrictMode> */}
      </Suspense>
    </BrowserRouter>
  </HelmetProvider>,
);
